import { WebSocketConnection } from './WebSocketConnection';
import { MessageHandler, WebSocketStatus, WSEventCategory } from './types';
import { parseWebSocketMessage, buildWebSocketURL } from './utils';
import { MessageHeader, RequestID } from '@/api/spec';

interface Subscription {
  id: string;
  onMessage: MessageHandler;
  onStatusChange: (status: WebSocketStatus, retryCount: number) => void;
  onError?: (category: WSEventCategory, code: number, reason: string) => void;
}

interface Connection {
  ws: WebSocketConnection;
  subscriptions: Map<string, Subscription>;
  sn: number;
}

export class WebSocketManager {
  private static instance: WebSocketManager;
  private connections = new Map<string, Connection>();
  private nextId = 1;

  private static readonly MAX_SEQUENCE_NUMBER = 0xffffffff;

  static getInstance(): WebSocketManager {
    return (this.instance ??= new WebSocketManager());
  }

  private handleDefaultError(endpoint: string, category: WSEventCategory, code: number, reason: string) {
    console.error(`[WebSocket] Error on ${endpoint}:`, {
      category,
      code,
      reason,
      timestamp: new Date().toISOString(),
    });
  }

  private sendMessageInternal<T extends MessageHeader>(endpoint: string, message: T): RequestID | undefined {
    const connection = this.connections.get(endpoint);
    if (!connection) {
      console.error(`[WebSocket] No connection found for endpoint: ${endpoint}`);
      return;
    }

    connection.sn = (connection.sn + 1) % WebSocketManager.MAX_SEQUENCE_NUMBER;
    const messageWithSn = message.sn ? message : { ...message, sn: connection.sn };

    if (connection.ws.sendJSON(messageWithSn)) {
      return message.sn || connection.sn;
    }
  }

  forceReconnect(endpoint: string): void {
    const connection = this.connections.get(endpoint);
    if (connection?.ws) {
      connection.ws.forceReconnect();
    }
  }

  subscribe(
    endpoint: string,
    onMessage: MessageHandler,
    onStatusChange: (status: WebSocketStatus, retryCount: number) => void,
    onError?: (category: WSEventCategory, code: number, reason: string) => void
  ) {
    const id = `${this.nextId++}`;
    const url = endpoint.startsWith('wss:') ? endpoint : buildWebSocketURL(endpoint);

    let connection = this.connections.get(endpoint);

    if (!connection) {
      const ws = new WebSocketConnection({
        url,
        onStatusChange: (status) => {
          const conn = this.connections.get(endpoint);
          if (conn) {
            conn.subscriptions.forEach((sub) => sub.onStatusChange(status, ws.getRetryCount()));
          }
        },
        onMessage: (event) => {
          const message = parseWebSocketMessage(event);
          if (message) {
            const conn = this.connections.get(endpoint);
            if (conn) {
              conn.subscriptions.forEach((sub) => sub.onMessage(message));
            }
          }
        },
        onError: (category, code, reason) => {
          const conn = this.connections.get(endpoint);
          if (conn) {
            let hasCustomHandler = false;
            conn.subscriptions.forEach((sub) => {
              if (sub.onError) {
                sub.onError(category, code, reason);
                hasCustomHandler = true;
              }
            });

            if (!hasCustomHandler) {
              this.handleDefaultError(endpoint, category, code, reason);
            }
          }
        },
      });

      connection = {
        ws,
        subscriptions: new Map(),
        sn: Date.now() % WebSocketManager.MAX_SEQUENCE_NUMBER,
      };

      this.connections.set(endpoint, connection);
    }

    // Add subscription
    connection.subscriptions.set(id, { id, onMessage, onStatusChange, onError });

    return {
      id,
      connect: () => connection!.ws.connect(),
      disconnect: () => this.unsubscribe(endpoint, id),
      send: <T extends MessageHeader>(data: T) => this.sendMessageInternal(endpoint, data),
      getStatus: () => connection!.ws.getStatus(),
      getRetryCount: () => connection!.ws.getRetryCount(),
    };
  }

  sendMessage<T extends MessageHeader>(endpoint: string, message: T): RequestID | undefined {
    return this.sendMessageInternal(endpoint, message);
  }

  private unsubscribe(endpoint: string, id: string): void {
    const connection = this.connections.get(endpoint);
    if (!connection) return;

    connection.subscriptions.delete(id);

    // Clean up if no more subscriptions
    if (connection.subscriptions.size === 0) {
      connection.ws.disconnect();
      this.connections.delete(endpoint);
    }
  }
}
